/*
 * ChatPage - AI Chat Interface with WhatsApp-style Smooth Keyboard Animations
 *
 * CRITICAL KEYBOARD ANIMATION IMPLEMENTATION:
 * This component implements smooth, flicker-free keyboard animations similar to WhatsApp.
 * Key components that MUST NOT BE MODIFIED without understanding the full system:
 *
 * 1. Native keyboard events: useKeyboardState hook in AppWrapper listens to
 *    keyboardWillShow/keyboardWillHide for both iOS and Android, providing predictive height
 * 2. Transform positioning: Input container uses translateY() instead of bottom positioning
 * 3. Capacitor configuration: KeyboardResize.None prevents automatic viewport changes
 * 4. Hardware acceleration: translateZ(0) and backface-visibility optimizations
 *
 * DO NOT revert to viewport-based detection or bottom positioning - causes flickering!
 */

import { FC, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { Spinner } from '@chakra-ui/react';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
import { colors, spacing, fontStyles, buildPageSpacing, borderRadius, fontFamilies } from '../../styles';
import { useNetworkStatus } from '../../utils/networkStatus';
import Page from '../Page';
import StickyHeader from '../StickyHeader';
import Button from '../Button';
import Header from '../Header';
import FeedbackTakeover from '../FeedbackTakeover';
import LoadingMessage from '../LoadingMessage';
import { getSuggestedQuestions, PortfolioContext } from '../../apis/backend';
import { getInvestorPersona } from '../MorePage/InvestorPersonaSelector';
import {
  getChatModelPreference,
  isPremiumModel,
  canUsePremiumModel,
  incrementPremiumModelUsage,
  decrementPremiumModelUsage,
  getPremiumModelUsage,
} from '../../utils/chatModelPreference';
import { CHAT_MODELS } from '../../constants/freemium';
import { appsflyerEvents } from '../../utils/appsflyerEvents';
import { Portfolios } from '../../storage';
import { useStreamingChat } from '../../hooks/useStreamingChat';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import * as stores from '../../stores';
import { useRatingDialog } from '../../hooks/useRatingDialog';
import { cleanMarkdownContent } from '../../utils/contentCleaning';
import { buildApiMessagesWithHybridToolOutputs, extractTextFromContent } from '../../utils/chatHelpers';
import { transformStockSymbols } from '../../utils/regexConstants';
import { ChatMessage, ChatThread } from '../../types/chat';
import {
  migrateOldChatHistory,
  loadThreads,
  loadActiveThreadId,
  saveThreads,
  saveActiveThreadId,
  createNewThread,
  generateThreadTitle,
} from '../../utils/chatHistoryUtils';
import { ChatHistoryDrawer } from '../ChatHistory';
import ChatCollectionCards from './ChatCollectionCards';
import ToolOutputDisplay from './ToolOutputDisplay';
import BloomIcon from './icon.webp';
import ChatInput from './ChatInput';
import { AttachedImage } from './ChatInput/ImageThumbnail';

// Daily message tracking interface
interface DailyMessageCount {
  count: number;
  date: string;
}

const MAX_FREE_MESSAGES = 3;

// Helper function to get today's date in user's timezone
const getTodayInUserTimezone = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
};

// Build portfolio context from the Portfolios store
// Limits: max 5 portfolios, 10 symbols per portfolio (matches backend display limit)
const MAX_PORTFOLIOS = 5;
const MAX_SYMBOLS_PER_PORTFOLIO = 10;

const buildPortfolioContext = (): PortfolioContext | undefined => {
  const portfolios = Portfolios.getRawState()?.portfolios;
  if (!portfolios || portfolios.length === 0) {
    return undefined;
  }

  return {
    portfolios: portfolios.slice(0, MAX_PORTFOLIOS).map((p) => {
      const symbols = (p.symbols || []).slice(0, MAX_SYMBOLS_PER_PORTFOLIO);
      const symbolSet = new Set(symbols);
      return {
        title: p.title,
        symbols,
        allocations: p.allocations
          ?.filter((a) => symbolSet.has(a.symbol))
          .map((a) => ({
            symbol: a.symbol,
            allocation_percentage: a.allocation_percentage,
          })),
      };
    }),
  };
};

const ChatPage: FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  // Thread-based state management
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Derived state: current thread's messages
  const activeThread = threads.find((t) => t.id === activeThreadId);
  const messages = useMemo(() => activeThread?.messages ?? [], [activeThread?.messages]);

  // Sprint 1a: Show thinking indicator immediately on send
  const [isSending, setIsSending] = useState(false);
  // Sprint 1d: Network status awareness
  const { isOnline, isInitialized: isNetworkInitialized } = useNetworkStatus();

  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialMessageHandled = useRef(false);
  // Sprint 2d: Track if currently streaming for background snapshot
  const isStreamingRef = useRef(false);
  // Ref kept in sync with threads on every render so retryPendingMessage's
  // alreadySent check always reads current threads even when called from the
  // mount effect (where the threads state is still [] from the initial render).
  const threadsRef = useRef<typeof threads>([]);
  threadsRef.current = threads;

  // CRITICAL: WhatsApp-style smooth keyboard animation implementation
  // These store values are populated by native keyboard events in index.tsx
  // DO NOT REMOVE: Prevents input flickering when keyboard appears/disappears
  const isKeyboardVisible = stores.General.useState((s) => s.isKeyboardVisible);
  const keyboardHeight = stores.General.useState((s) => s.keyboardHeight);

  const [messageCount, setMessageCount] = useState<number>(0);
  // Use subscription hook which respects dev overrides (?sub=pro, ?sub=trial)
  const { isSubscribed } = useSubscriptionStatus();
  const [isNavigatedFrom, setIsNavigatedFrom] = useState(false);
  const [retryTimestamp, setRetryTimestamp] = useState<string | null>(null);

  // Use streaming chat hook
  const {
    isLoading,
    progressText,
    streamingMessage,
    finalResponse,
    collectionCards,
    toolOutputs,
    latestTodoOutput,
    submitQuery,
    resetQuery,
  } = useStreamingChat();

  // Usar el hook de rating
  const { isFeedbackTakeoverShown, setIsFeedbackTakeoverShown, triggerRatingDialog } = useRatingDialog({
    skipCondition: isSubscribed,
    daysToWait: 3,
  });
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Keyboard events are now handled globally by useKeyboardState hook in AppWrapper
  // for both iOS and Android using keyboardWillShow/keyboardWillHide events.
  // This eliminates the need for duplicate listeners here.
  //
  // Reset keyboard state when leaving ChatPage to ensure NavigationBar is visible
  useEffect(() => {
    return () => {
      stores.General.update((s) => {
        s.isKeyboardVisible = false;
        s.keyboardHeight = 0;
      });
    };
  }, []);

  // Memoize message processing to prevent re-computation on every render
  const displayMessages = messages;

  // Helper to update messages in the active thread
  const updateActiveThreadMessages = useCallback(
    (updater: (prevMessages: ChatMessage[]) => ChatMessage[]) => {
      setThreads((prevThreads) => {
        // If no active thread, create one
        if (!activeThreadId) {
          const newThread = createNewThread();
          const newMessages = updater([]);
          newThread.messages = newMessages;
          newThread.title = generateThreadTitle(newMessages);
          newThread.updatedAt = new Date().toISOString();
          setActiveThreadId(newThread.id);
          return [...prevThreads, newThread];
        }

        return prevThreads.map((thread) => {
          if (thread.id === activeThreadId) {
            const newMessages = updater(thread.messages);
            return {
              ...thread,
              messages: newMessages,
              title: thread.messages.length === 0 ? generateThreadTitle(newMessages) : thread.title,
              updatedAt: new Date().toISOString(),
            };
          }
          return thread;
        });
      });
    },
    [activeThreadId],
  );

  // Sprint 1c: Update message status by timestamp (no dependency on activeThreadId)
  // Optimized: only create new thread/message objects when a message actually matches
  const updateMessageStatus = useCallback((timestamp: string, status: 'sending' | 'sent' | 'failed') => {
    setThreads((prevThreads) =>
      prevThreads.map((thread) => {
        // Check if any message in this thread matches before spreading
        const hasMatch = thread.messages.some((m) => m.timestamp === timestamp && m.role === 'user');
        if (!hasMatch) return thread;
        return {
          ...thread,
          messages: thread.messages.map((m) => (m.timestamp === timestamp && m.role === 'user' ? { ...m, status } : m)),
        };
      }),
    );
  }, []);

  // Load threads on mount (with migration from old format)
  useEffect(() => {
    // Try migration first (handles old chatHistory format)
    const migrated = migrateOldChatHistory();
    if (migrated) {
      // Fix: keep threadsRef in sync immediately so the mount retry effect (which
      // runs after this one) sees the real threads when performing the alreadySent
      // check, even before React has flushed the setThreads state update.
      threadsRef.current = migrated.threads;
      setThreads(migrated.threads);
      setActiveThreadId(migrated.activeThreadId);
    } else {
      // Load from new format
      const savedThreads = loadThreads();
      const savedActiveId = loadActiveThreadId();
      // Fix: keep threadsRef in sync immediately so the mount retry effect (which
      // runs after this one) sees the real threads when performing the alreadySent
      // check, even before React has flushed the setThreads state update.
      threadsRef.current = savedThreads;
      setThreads(savedThreads);

      // Set active thread: saved ID, or most recent, or null
      if (savedActiveId && savedThreads.find((t) => t.id === savedActiveId)) {
        setActiveThreadId(savedActiveId);
      } else if (savedThreads.length > 0) {
        // Sort by updatedAt and pick most recent
        const sorted = [...savedThreads].sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
        setActiveThreadId(sorted[0].id);
      }
    }

    // Load suggestions
    const savedSuggestions = localStorage.getItem('chatSuggestions');
    if (savedSuggestions) {
      setSuggestedQuestions(JSON.parse(savedSuggestions));
    }

    // Load daily message count
    const savedDailyCount = localStorage.getItem('dailyMessageCount');
    if (savedDailyCount) {
      const dailyCount: DailyMessageCount = JSON.parse(savedDailyCount);
      const today = getTodayInUserTimezone();

      if (dailyCount.date === today) {
        setMessageCount(dailyCount.count);
      } else {
        // Reset count for new day
        setMessageCount(0);
        localStorage.setItem('dailyMessageCount', JSON.stringify({ count: 0, date: today }));
      }
    }
  }, []);

  // Save threads whenever they change (only save threads with messages)
  useEffect(() => {
    const threadsWithMessages = threads.filter((t) => t.messages.length > 0);
    if (threadsWithMessages.length > 0) {
      const result = saveThreads(threadsWithMessages);
      if (result.trimmed) {
        toast(t('chat.storageLimitReached'), {
          toastId: 'storage-trimmed', // Prevent duplicate toasts
        });
      }
    }
  }, [threads, t]);

  // Save active thread ID whenever it changes
  useEffect(() => {
    saveActiveThreadId(activeThreadId);
  }, [activeThreadId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isSending, isLoading, streamingMessage, finalResponse, collectionCards]);

  // Sprint 2d: App background/kill recovery
  // Fix #5: Use refs for volatile values so this listener doesn't re-register on every streaming token
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;
  // Track isNetworkInitialized via ref so the mount-retry effect can guard without
  // adding it to deps (which would cause an extra run and break the single-fire intent).
  const isNetworkInitializedRef = useRef(isNetworkInitialized);
  isNetworkInitializedRef.current = isNetworkInitialized;
  const messageCountRef = useRef(messageCount);
  messageCountRef.current = messageCount;

  // DRY: Shared helper for auto-retry of pending messages
  // Sets isStreamingRef guard to prevent concurrent retries (race condition fix)
  const retryPendingMessage = useCallback(
    async (threadId: string | null) => {
      if (isLoadingRef.current || isStreamingRef.current) return;

      // Intentionally defer guard acquisition until AFTER the Preferences lookup so
      // that an empty-result early return does NOT hold the guard and block the next
      // invocation.  Concretely: the mount-retry effect fires twice — first with
      // activeThreadId=null (no pending message), then immediately after with the real
      // thread ID (once the loadThreads effect has committed its setActiveThreadId state
      // update).  If the guard were set before the async read, the second call would
      // find the guard held by the still-awaiting first call and silently bail out.
      //
      // Safety: JavaScript is single-threaded, so the re-check + set below is atomic
      // with respect to any other synchronous code path.

      // --- Phase 1: resolve the pending key/value (guard NOT held yet) ---
      let pendingKey = threadId ? `pending_msg_${threadId}` : 'pending_msg_new';
      let pendingValue: string | null = null;
      try {
        const pending = await Preferences.get({ key: pendingKey });
        pendingValue = pending.value;
        if (!pendingValue && threadId) {
          // Check the fallback key for messages persisted before the thread ID was known
          const pendingNew = await Preferences.get({ key: 'pending_msg_new' });
          if (pendingNew.value) {
            pendingKey = 'pending_msg_new';
            pendingValue = pendingNew.value;
          } else {
            return; // No pending message — guard never acquired, next call is unblocked
          }
        }
        if (!pendingValue || !isOnlineRef.current) {
          return; // No pending message or offline — guard never acquired, next call is unblocked
        }
      } catch {
        return; // Preferences read failed — nothing to retry
      }

      // --- Phase 2: acquire guard then process (re-check after the async gap above) ---
      // Re-check after the async Preferences read: a concurrent trigger (e.g.
      // appStateChange + online) may have started processing by now.
      if (isStreamingRef.current) return;
      isStreamingRef.current = true;

      try {
        let pendingData: any;
        try {
          pendingData = JSON.parse(pendingValue);
        } catch {
          console.error('Corrupted pending message JSON, removing key');
          await Preferences.remove({ key: pendingKey });
          return;
        }

        // Validate that pendingData is a non-null object with a valid messages array.
        // JSON.parse("null") returns null (no exception), causing pendingData.userMessage to throw.
        // Malformed messages (non-array) would cause repeated failed retries without cleanup.
        if (!pendingData || typeof pendingData !== 'object' || !Array.isArray(pendingData.messages)) {
          console.error('Invalid pending message data structure, removing key');
          try {
            // pendingKey is already 'pending_msg_new' when we used the fallback key, so
            // this remove covers both cases. Don't also remove 'pending_msg_new' separately —
            // that would silently delete a pending message from a different new thread.
            await Preferences.remove({ key: pendingKey });
          } catch (e) {
            console.error('Failed to clear invalid pending message:', e);
          }
          return;
        }

        const userMsg = pendingData.userMessage;

        // Compute once: was this message originally sent today?
        // Both the alreadySent branch and the success branch need this check.
        const sentToday = !pendingData.createdAt
          ? true // legacy: no date stored, assume same day to be conservative
          : (() => {
              const msgDate = new Date(pendingData.createdAt);
              const now = new Date();
              return (
                msgDate.getFullYear() === now.getFullYear() &&
                msgDate.getMonth() === now.getMonth() &&
                msgDate.getDate() === now.getDate()
              );
            })();

        // Skip retry if the message was already sent successfully (crash between
        // SSE completion and Preferences cleanup). Check if the user message
        // timestamp already has a following assistant response in the current threads.
        if (userMsg?.timestamp) {
          const currentThreads = threadsRef.current;
          const alreadySent = currentThreads.some((t) =>
            t.messages.some(
              (m, i, arr) => m.timestamp === userMsg.timestamp && m.role === 'user' && arr[i + 1]?.role === 'assistant',
            ),
          );
          if (alreadySent) {
            // Clean up stale pending key. Only increment the count if the original
            // handleSendMessage run did NOT already do so — detected via countWhenSent:
            //   • countWhenSent absent (legacy format) → already incremented by original send, skip
            //   • countWhenSent present AND current count > countWhenSent → already incremented, skip
            //   • countWhenSent present AND current count == countWhenSent → not yet incremented, do it
            // This prevents double-incrementing when a crash occurred between a successful
            // response and the Preferences.remove call that would have cleared the pending key.
            try {
              await Preferences.remove({ key: pendingKey });
              // pendingKey covers 'pending_msg_new' when that's what was used; don't also
              // remove it unconditionally — that would clobber another new thread's pending.
            } catch (e) {
              console.error('Failed to clear stale pending message:', e);
            }
            const countWhenSent: number | undefined = pendingData.countWhenSent;

            // Don't charge yesterday's messages against today's quota (sentToday computed above)
            const alreadyCounted =
              typeof countWhenSent !== 'number' ||
              !sentToday || // cross-day recovery: never count against today's quota
              messageCountRef.current > countWhenSent;
            if (!alreadyCounted) {
              const newCount = messageCountRef.current + 1;
              setMessageCount(newCount);
              const today = getTodayInUserTimezone();
              localStorage.setItem('dailyMessageCount', JSON.stringify({ count: newCount, date: today }));
              if (!isSubscribed && newCount === 3) {
                triggerRatingDialog();
              }
            }
            return;
          }

          updateMessageStatus(userMsg.timestamp, 'sending');
        }

        // Check free-tier daily limit — don't auto-retry over quota
        if (!isSubscribed && messageCountRef.current >= MAX_FREE_MESSAGES) {
          if (userMsg?.timestamp) updateMessageStatus(userMsg.timestamp, 'failed');
          // Clear pending message so repeated app resumes don't keep hitting the limit
          try {
            await Preferences.remove({ key: pendingKey }); // covers 'pending_msg_new' when used
          } catch (e) {
            console.error('Failed to clear pending message on free limit:', e);
          }
          return;
        }

        // Pass onConnected so status flips to 'sent' when SSE connects (consistent with handleSendMessage)
        const onConnected = userMsg?.timestamp ? () => updateMessageStatus(userMsg.timestamp, 'sent') : undefined;

        const success = await submitQuery(
          pendingData.messages,
          pendingData.portfolioContext,
          pendingData.investorPersona,
          pendingData.model,
          onConnected,
          pendingData.language,
        );
        if (success) {
          if (userMsg?.timestamp) {
            updateMessageStatus(userMsg.timestamp, 'sent');
          } else {
            // Pending data missing userMessage or timestamp — can't update a specific message.
            // Log so this is trackable; the assistant response will still appear correctly.
            console.warn('retryPendingMessage: success but no userMessage timestamp to update status');
          }
          try {
            await Preferences.remove({ key: pendingKey }); // covers 'pending_msg_new' when used
          } catch (e) {
            console.error('Failed to clear pending message:', e);
          }
          // Only charge today's quota if the message was originally sent today.
          // Cross-day retries (message created yesterday, retried after midnight) must
          // not consume the new day's free message allowance — sentToday computed above.
          if (sentToday) {
            // Increment message count for auto-retried messages to prevent free-tier bypass
            const newCount = messageCountRef.current + 1;
            setMessageCount(newCount);
            const today = getTodayInUserTimezone();
            localStorage.setItem('dailyMessageCount', JSON.stringify({ count: newCount, date: today }));
            if (!isSubscribed && newCount === 3) {
              triggerRatingDialog();
            }
          }
        } else {
          if (userMsg?.timestamp) updateMessageStatus(userMsg.timestamp, 'failed');
        }
      } finally {
        isStreamingRef.current = false;
      }
    },
    [submitQuery, updateMessageStatus, isSubscribed, triggerRatingDialog],
  );

  useEffect(() => {
    let disposed = false;

    const listener = App.addListener('appStateChange', async ({ isActive }: { isActive: boolean }) => {
      if (!isActive || disposed) return;
      await retryPendingMessage(activeThreadId);
    });

    return () => {
      disposed = true;
      listener
        ?.then((l: any) => l?.remove?.())
        .catch((error) => console.error('Failed to remove appStateChange listener:', error));
    };
  }, [activeThreadId, retryPendingMessage]);

  // Sprint 2c: On mount, check for pending messages and auto-retry
  // Supports both thread-specific and 'new' pending messages (null activeThreadId)
  // Guard: only run once network + quota are initialized so free-tier check reads
  // the real count from localStorage (not the default 0 before hydration).
  useEffect(() => {
    if (!isNetworkInitializedRef.current) return;
    retryPendingMessage(activeThreadId);
  }, [activeThreadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-trigger pending retry once network status is initialized (covers the case
  // where mount fired before Network.getStatus() resolved and was skipped above).
  useEffect(() => {
    if (isNetworkInitialized) {
      retryPendingMessage(activeThreadId);
    }
  }, [isNetworkInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-retry pending messages when Capacitor network state transitions to online
  // Uses isOnline state (Capacitor Network plugin) instead of window 'online' event,
  // which does not fire reliably on iOS/WKWebView.
  const prevIsOnlineRef = useRef(isOnline);
  useEffect(() => {
    if (isOnline && !prevIsOnlineRef.current) {
      retryPendingMessage(activeThreadId);
    }
    prevIsOnlineRef.current = isOnline;
  }, [isOnline, activeThreadId, retryPendingMessage]);

  useEffect(() => {
    localStorage.setItem('chatSuggestions', JSON.stringify(suggestedQuestions));
  }, [suggestedQuestions]);

  // Handle finalResponse and collectionCards from streaming
  useEffect(() => {
    // Only process when we have a final response (streaming is complete)
    // Collection cards might arrive before or after, but we wait for finalResponse
    if (!finalResponse) return;

    // Create the assistant message with whatever data we have
    // Process text content
    const processedContent = transformStockSymbols(finalResponse);

    // Combine tool outputs: latest todo (if exists) + other tools
    const consolidatedToolOutputs = [];
    if (latestTodoOutput) {
      consolidatedToolOutputs.push(latestTodoOutput);
    }
    consolidatedToolOutputs.push(...toolOutputs);

    // Create message with content and optionally with collection cards and tool outputs
    const assistantMessage: ChatMessage = {
      role: 'assistant',
      content: processedContent,
      timestamp: new Date().toISOString(),
      ...(collectionCards && { collectionCards: collectionCards }),
      ...(consolidatedToolOutputs.length > 0 && { toolOutputs: consolidatedToolOutputs }),
    };

    updateActiveThreadMessages((prev) => {
      // Prevent duplicate messages by checking if we just added this
      const lastMessage = prev[prev.length - 1];
      if (lastMessage?.role === 'assistant' && lastMessage?.content === processedContent) {
        // Update existing message with collection cards if they arrived later
        if (collectionCards && !lastMessage.collectionCards) {
          return [...prev.slice(0, -1), { ...lastMessage, collectionCards: collectionCards }];
        }
        return prev; // No change needed
      }

      const newMessages = [...prev, assistantMessage];

      // Fetch suggested questions with the new messages
      // Use hybrid approach for cost optimization
      const apiMessages = buildApiMessagesWithHybridToolOutputs(newMessages);
      fetchSuggestedQuestions(apiMessages);

      return newMessages;
    });
  }, [finalResponse, collectionCards, toolOutputs, latestTodoOutput, updateActiveThreadMessages]); // Dependencies are OK - the logic inside prevents duplicates

  const fetchSuggestedQuestions = async (apiMessages: ChatMessage[]) => {
    try {
      setIsSuggestionsLoading(true);
      const suggestionsResponse = await getSuggestedQuestions(apiMessages);
      setSuggestedQuestions(suggestionsResponse.questions);
    } catch (error) {
      console.error('Suggestions API error:', error);
    } finally {
      setIsSuggestionsLoading(false);
    }
  };

  const handleSendMessage = useCallback(
    async (messageText: string, images: AttachedImage[] = []) => {
      if (!messageText && images.length === 0) return;

      if (!isSubscribed && messageCount >= MAX_FREE_MESSAGES) {
        const newUserMessage: ChatMessage = {
          role: 'user',
          content: messageText,
          timestamp: new Date().toISOString(),
        };
        const subscriptionMessage: ChatMessage = {
          role: 'assistant',
          content: t('chat.limitReached'),
          timestamp: new Date().toISOString(),
        };
        updateActiveThreadMessages((prev) => [...prev, newUserMessage, subscriptionMessage]);
        return;
      }

      // Sprint 1a: Set isSending immediately BEFORE any async work
      setIsSending(true);

      // Track the user message timestamp so a catch block can mark it 'failed'
      // if a sync throw occurs after the message is painted but before submitQuery.
      let pendingUserTimestamp: string | undefined;

      try {
        // Sprint 1c: Add user message with status: 'sending'
        const newUserMessage: ChatMessage = {
          role: 'user',
          content: messageText,
          timestamp: new Date().toISOString(),
          images: images.length > 0 ? images : undefined,
          status: 'sending',
        };
        pendingUserTimestamp = newUserMessage.timestamp;
        const updatedMessages = [...messages, newUserMessage];
        updateActiveThreadMessages((prev) => [...prev, newUserMessage]);
        setSuggestedQuestions([]);

        // Sprint 3b: Smooth scroll to bottom after appending user message
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        });

        // Track chat interaction with AppsFlyer
        appsflyerEvents.research.chatMessageSent(messageText.length, updatedMessages.length).catch(console.error);

        // Sprint 1b: Yield to let React paint user message + thinking indicator
        await new Promise((resolve) => setTimeout(resolve, 0));

        // Use hybrid tool output strategy for cost optimization
        const apiMessages = buildApiMessagesWithHybridToolOutputs(updatedMessages);

        // Build portfolio context for personalized AI responses
        const portfolioContext = buildPortfolioContext();
        const investorPersona = getInvestorPersona() ?? undefined;

        // Get selected chat model and check premium model limits
        // Note: isSubscribed respects dev overrides via useSubscriptionStatus (?sub=pro)
        const selectedModel = getChatModelPreference() ?? undefined;
        let usedPremiumModel = false;
        if (selectedModel && isPremiumModel(selectedModel)) {
          // Premium models require active subscription
          if (!isSubscribed) {
            const limitMessage: ChatMessage = {
              role: 'assistant',
              content: t('chat.premiumRequired'),
              timestamp: new Date().toISOString(),
            };
            updateActiveThreadMessages((prev) => [...prev, limitMessage]);
            // Fix #1: Update message status before returning so it doesn't stay stuck on 'sending'
            updateMessageStatus(newUserMessage.timestamp!, 'sent');
            return;
          }
          if (!canUsePremiumModel()) {
            const limitMessage: ChatMessage = {
              role: 'assistant',
              content: t('chat.dailyLimitReached'),
              timestamp: new Date().toISOString(),
            };
            updateActiveThreadMessages((prev) => [...prev, limitMessage]);
            // Fix #1: Update message status before returning so it doesn't stay stuck on 'sending'
            updateMessageStatus(newUserMessage.timestamp!, 'sent');
            return;
          }
          incrementPremiumModelUsage();
          usedPremiumModel = true;
        }

        // Sprint 2c: Persist pending message to Capacitor Storage before network request
        // Fix #2/#4: Use a well-known key when activeThreadId is null (new conversation).
        // Previously we searched threadsRef.current, but the setThreads update from
        // updateActiveThreadMessages hasn't committed yet, so the search always missed.
        // Use 'pending_msg_new' so retryPendingMessage can find it via the fallback check.
        const threadId = activeThreadId || 'new';

        // Set guard BEFORE Preferences.set to close a race window where an 'online' event
        // could trigger retryPendingMessage between Preferences.set completing and the
        // isStreamingRef.current = true assignment, causing a duplicate submitQuery call.
        isStreamingRef.current = true;

        // Fix #3/#10: Wrap Preferences writes in try/catch so failures don't leave isSending stuck
        try {
          await Preferences.set({
            key: `pending_msg_${threadId}`,
            value: JSON.stringify({
              messages: apiMessages,
              userMessage: newUserMessage,
              portfolioContext,
              investorPersona,
              model: selectedModel,
              language: i18n.language,
              createdAt: Date.now(),
              // Snapshot the count at send time so retryPendingMessage can detect
              // whether the original run already incremented it (prevents double-count
              // when a crash occurs between the successful response and key removal).
              countWhenSent: messageCountRef.current,
            }),
          });
        } catch (e) {
          console.error('Failed to persist pending message:', e);
          updateMessageStatus(newUserMessage.timestamp!, 'failed');
          isStreamingRef.current = false;
          return;
        }

        // Sprint 1c: onConnected callback updates message status to 'sent'
        const msgTimestamp = newUserMessage.timestamp!;
        const onConnected = () => {
          updateMessageStatus(msgTimestamp, 'sent');
        };

        let success = false;
        try {
          success = await submitQuery(
            apiMessages,
            portfolioContext,
            investorPersona,
            selectedModel,
            onConnected,
            i18n.language,
          );

          if (success) {
            // Sprint 1c: Mark sent on success, Sprint 2c: clear pending message
            updateMessageStatus(msgTimestamp, 'sent');
            try {
              // When threadId='new', pending_msg_${threadId} IS pending_msg_new so first
              // remove covers it. When threadId is a real UUID, don't touch pending_msg_new
              // — it may belong to a different new thread.
              await Preferences.remove({ key: `pending_msg_${threadId}` });
            } catch (e) {
              console.error('Failed to clear pending message:', e);
            }
            // Only count messages that were successfully sent to avoid inflating
            // the free message count and prematurely triggering the rating dialog
            const newCount = messageCountRef.current + 1;
            setMessageCount(newCount);
            const today = getTodayInUserTimezone();
            localStorage.setItem('dailyMessageCount', JSON.stringify({ count: newCount, date: today }));
            if (!isSubscribed && newCount === 3) {
              triggerRatingDialog();
            }
          } else {
            // Sprint 1c: Mark failed on error
            if (usedPremiumModel) {
              decrementPremiumModelUsage();
            }
            updateMessageStatus(msgTimestamp, 'failed');
          }
        } finally {
          isStreamingRef.current = false;
        }
      } catch (err) {
        // Sync throw after user message was painted — mark it failed so it's not stuck in 'sending'
        if (pendingUserTimestamp) {
          updateMessageStatus(pendingUserTimestamp, 'failed');
        }
        isStreamingRef.current = false;
        console.error('handleSendMessage unexpected error:', err);
      } finally {
        setIsSending(false);
      }
    },
    [
      messages,
      isSubscribed,
      messageCount,
      triggerRatingDialog,
      submitQuery,
      updateActiveThreadMessages,
      updateMessageStatus,
      activeThreadId,
      t,
      i18n.language,
    ],
  );

  const handleRetry = useCallback(async () => {
    if (!retryTimestamp || isLoading || isStreamingRef.current) return;
    // Capture timestamp in a local const before any state changes to avoid stale closure
    // issues if the component re-renders mid-async while retryTimestamp resets to null.
    const ts = retryTimestamp;
    const index = messages.findIndex((m) => m.timestamp === ts);
    if (index === -1) return;

    const retryMessages = messages.slice(0, index + 1);

    // Check free-tier daily limit — don't allow retry over quota (mirrors handleSendMessage)
    if (!isSubscribed && messageCount >= MAX_FREE_MESSAGES) {
      const limitMessage: ChatMessage = {
        role: 'assistant',
        content: t('chat.limitReached'),
        timestamp: new Date().toISOString(),
      };
      updateActiveThreadMessages((prev) => [...prev, limitMessage]);
      updateMessageStatus(ts, 'sent');
      setRetryTimestamp(null);
      try {
        // Only remove 'pending_msg_new' when activeThreadId is null — that's when it was used.
        // Removing it unconditionally would silently delete a different new-thread's pending.
        if (activeThreadId) await Preferences.remove({ key: `pending_msg_${activeThreadId}` });
        else await Preferences.remove({ key: 'pending_msg_new' });
      } catch (e) {
        console.error('Failed to clear pending message on free limit:', e);
      }
      return;
    }

    setSuggestedQuestions([]);

    // Fix #8: Update the failed message status to 'sending' and remove any
    // stale error assistant messages that were appended after the failed user message.
    // Without this, the finalResponse useEffect's error message persists alongside
    // the new assistant response after a successful retry.
    updateActiveThreadMessages((prev) => {
      const idx = prev.findIndex((m) => m.timestamp === ts);
      if (idx === -1) return prev;
      // Keep messages up to and including the retried user message, drop everything after
      const trimmed = prev.slice(0, idx + 1);
      // Update the user message status to 'sending'
      return trimmed.map((m) => (m.timestamp === ts && m.role === 'user' ? { ...m, status: 'sending' as const } : m));
    });
    setRetryTimestamp(null);

    // Use hybrid tool output strategy for retry (same as handleSendMessage)
    const apiMessages = buildApiMessagesWithHybridToolOutputs(retryMessages);

    // Build portfolio context for personalized AI responses
    const portfolioContext = buildPortfolioContext();
    const investorPersona = getInvestorPersona() ?? undefined;

    // Get selected chat model and check premium model limits
    // Note: isSubscribed respects dev overrides via useSubscriptionStatus (?sub=pro)
    const selectedModel = getChatModelPreference() ?? undefined;
    let usedPremiumModel = false;
    if (selectedModel && isPremiumModel(selectedModel)) {
      // Premium models require active subscription
      if (!isSubscribed) {
        const limitMessage: ChatMessage = {
          role: 'assistant',
          content: t('chat.premiumRequired'),
          timestamp: new Date().toISOString(),
        };
        updateActiveThreadMessages((prev) => [...prev, limitMessage]);
        updateMessageStatus(ts, 'sent');
        setRetryTimestamp(null);
        try {
          if (activeThreadId) await Preferences.remove({ key: `pending_msg_${activeThreadId}` });
          else await Preferences.remove({ key: 'pending_msg_new' });
        } catch (e) {
          console.error('Failed to clear pending message on limit:', e);
        }
        return;
      }
      if (!canUsePremiumModel()) {
        const limitMessage: ChatMessage = {
          role: 'assistant',
          content: t('chat.dailyLimitReached'),
          timestamp: new Date().toISOString(),
        };
        updateActiveThreadMessages((prev) => [...prev, limitMessage]);
        updateMessageStatus(ts, 'sent');
        setRetryTimestamp(null);
        try {
          if (activeThreadId) await Preferences.remove({ key: `pending_msg_${activeThreadId}` });
          else await Preferences.remove({ key: 'pending_msg_new' });
        } catch (e) {
          console.error('Failed to clear pending message on limit:', e);
        }
        return;
      }
      // Increment usage before sending (optimistic)
      incrementPremiumModelUsage();
      usedPremiumModel = true;
    }

    isStreamingRef.current = true;

    // Retrieve the original language from the persisted pending message so retry uses
    // the same language as the original send, not the current i18n.language which may
    // have changed between the failed send and the manual retry tap.
    let retryLanguage = i18n.language;
    try {
      const pendingKey = activeThreadId ? `pending_msg_${activeThreadId}` : 'pending_msg_new';
      const pending = await Preferences.get({ key: pendingKey });
      // Also check pending_msg_new fallback: original send may have stored under 'new' key
      // when activeThreadId was null, but by retry time activeThreadId is set.
      let pendingValue = pending.value;
      if (!pendingValue && activeThreadId) {
        const pendingNew = await Preferences.get({ key: 'pending_msg_new' });
        pendingValue = pendingNew.value;
      }
      if (pendingValue) {
        const pendingData = JSON.parse(pendingValue);
        if (pendingData?.language) {
          retryLanguage = pendingData.language;
        }
      }
    } catch {
      // Fall back to current i18n.language if Preferences read fails
    }

    // Pass onConnected so status flips to 'sent' when SSE connects (consistent with handleSendMessage)
    const onConnected = () => {
      updateMessageStatus(ts, 'sent');
    };

    let success = false;
    try {
      success = await submitQuery(
        apiMessages,
        portfolioContext,
        investorPersona,
        selectedModel,
        onConnected,
        retryLanguage,
      );
      // Fix #8: Update message status based on retry result
      if (success) {
        updateMessageStatus(ts, 'sent');
        // Clear persisted pending message on successful retry to prevent
        // stale auto-retry on next app mount/resume (causes duplicate messages)
        try {
          if (activeThreadId) await Preferences.remove({ key: `pending_msg_${activeThreadId}` });
          else await Preferences.remove({ key: 'pending_msg_new' }); // null activeThreadId → was stored under this key
        } catch (e) {
          console.error('Failed to clear pending message:', e);
        }
        // Count the retried message toward the daily free limit (same as handleSendMessage)
        const newCount = messageCountRef.current + 1;
        setMessageCount(newCount);
        const today = getTodayInUserTimezone();
        localStorage.setItem('dailyMessageCount', JSON.stringify({ count: newCount, date: today }));
        if (!isSubscribed && newCount === 3) {
          triggerRatingDialog();
        }
      } else {
        updateMessageStatus(ts, 'failed');
        // Rollback premium model usage on failure
        if (usedPremiumModel) {
          decrementPremiumModelUsage();
        }
      }
    } finally {
      isStreamingRef.current = false;
    }
  }, [
    retryTimestamp,
    messages,
    isLoading,
    isSubscribed,
    messageCount,
    triggerRatingDialog,
    activeThreadId,
    submitQuery,
    updateActiveThreadMessages,
    updateMessageStatus,
    t,
    i18n.language,
  ]);

  useEffect(() => {
    const state = location.state as {
      message?: string;
      from?: string;
      initialPrompt?: string;
      fromOnboarding?: boolean;
      autoStart?: boolean;
      onboardingContext?: any;
    };

    // Handle URL query parameters (e.g., ?question=...)
    const urlParams = new URLSearchParams(location.search);
    const questionFromUrl = urlParams.get('question');

    // Handle onboarding completion with immediate AI response
    if (state?.fromOnboarding && state?.initialPrompt && !initialMessageHandled.current) {
      initialMessageHandled.current = true;
      // Always auto-send when there's an initial prompt from onboarding
      handleSendMessage(state.initialPrompt);
      // Clear location state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
    // Handle legacy message state
    else if (state?.message && !initialMessageHandled.current) {
      initialMessageHandled.current = true;
      handleSendMessage(state.message);
      window.history.replaceState({}, document.title);
    }
    // Handle question from URL query parameter
    else if (questionFromUrl && !initialMessageHandled.current) {
      initialMessageHandled.current = true;
      // Auto-send the message from URL
      handleSendMessage(questionFromUrl);
      // Clear the URL parameter to prevent re-triggering
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('question');
      window.history.replaceState({}, document.title, newUrl.toString());
    }

    // Check if navigated from somewhere (not directly to chat tab)
    // Don't show back button if navigating from onboarding completion
    const onboardingCompleted = localStorage.getItem('hasCompletedOnboarding') === 'true';
    const isFromOnboarding = state?.fromOnboarding;

    // Show back button if navigated from elsewhere, unless it's from completing onboarding
    if ((state?.from || location.state || questionFromUrl) && !(isFromOnboarding && onboardingCompleted)) {
      setIsNavigatedFrom(true);
    }
  }, [location.state, location.search, handleSendMessage]);

  // Thread management handlers
  const handleNewChat = () => {
    // Don't create thread yet - it will be created when first message is sent
    // (see updateActiveThreadMessages which creates a thread when activeThreadId is null)
    setActiveThreadId(null);
    setSuggestedQuestions([]);
    localStorage.removeItem('chatSuggestions');
    resetQuery();
    setDefaultQuestions(getRandomQuestions(questionPool));
    setIsDrawerOpen(false);
  };

  const handleSelectThread = (threadId: string) => {
    setActiveThreadId(threadId);
    setSuggestedQuestions([]);
    resetQuery();
    setIsDrawerOpen(false);
  };

  const handleDeleteThread = (threadId: string) => {
    setThreads((prev) => {
      const filtered = prev.filter((t) => t.id !== threadId);

      // If deleting the active thread, switch to most recent
      if (threadId === activeThreadId) {
        if (filtered.length > 0) {
          const sorted = [...filtered].sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          );
          setActiveThreadId(sorted[0].id);
        } else {
          setActiveThreadId(null);
        }
      }

      return filtered;
    });
    resetQuery();
  };

  // Pool of all available questions: display is translated, prompt is always English for the AI backend
  const questionPool: { display: string; prompt: string }[] = [
    // Existing questions
    { display: t('chat.suggestedQ.whatCanIDo'), prompt: 'What can I do with Bloom?' },
    { display: t('chat.suggestedQ.summarizeNews'), prompt: 'Summarize the latest news for Netflix' },
    { display: t('chat.suggestedQ.whatToInvest'), prompt: 'What could I invest in?' },
    { display: t('chat.suggestedQ.isAppleGoodBuy'), prompt: 'Is Apple a good buy?' },
    // New questions that test specific bloom_agent tools
    { display: t('chat.suggestedQ.analyzeNvidiaChart'), prompt: "Analyze NVIDIA's price chart" },
    { display: t('chat.suggestedQ.marketSentiment'), prompt: "What's the current market sentiment?" },
    { display: t('chat.suggestedQ.earningsCallAI'), prompt: 'Find earnings call mentions of AI revenue growth' },
    { display: t('chat.suggestedQ.microsoftEarnings'), prompt: "Analyze Microsoft's latest earnings results" },
    { display: t('chat.suggestedQ.amazonTechnicals'), prompt: 'What do the technical indicators show for Amazon?' },
    { display: t('chat.suggestedQ.analyzePortfolio'), prompt: 'Analyze my portfolio' },
    { display: t('chat.suggestedQ.sellApple'), prompt: 'Is selling Apple a good trade?' },
    { display: t('chat.suggestedQ.lowPE'), prompt: 'Find stocks with less than 15 P/E' },
    { display: t('chat.suggestedQ.investingIdeas'), prompt: 'Give me investing ideas' },
    { display: t('chat.suggestedQ.netflixCatalysts'), prompt: 'What are potential catalysts for Netflix?' },
    // Market map questions
    { display: t('chat.suggestedQ.topMovers'), prompt: "Show me today's top movers" },
    { display: t('chat.suggestedQ.bestSectors'), prompt: 'What sectors are performing best this week?' },
    { display: t('chat.suggestedQ.globalMarkets'), prompt: 'How are global markets doing?' },
    { display: t('chat.suggestedQ.majorIndexes'), prompt: 'How are major indexes performing this year?' },
  ];

  // Randomly select 4 questions from the pool
  const getRandomQuestions = (pool: { display: string; prompt: string }[], count = 4) => {
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  };

  const [defaultQuestions, setDefaultQuestions] = useState(() => getRandomQuestions(questionPool));

  // Re-roll suggested questions when language changes so display text updates
  useEffect(() => {
    setDefaultQuestions(getRandomQuestions(questionPool));
  }, [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Page showNavigationBar>
      {/* Chat History Drawer - only show threads with messages */}
      <ChatHistoryDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        threads={threads.filter((t) => t.messages.length > 0)}
        activeThreadId={activeThreadId}
        onSelectThread={handleSelectThread}
        onDeleteThread={handleDeleteThread}
        onNewChat={handleNewChat}
      />

      {/* CRITICAL: Main container with dynamic padding for smooth keyboard animation
          DO NOT REMOVE: This padding calculation prevents content jumping when keyboard appears
          - Uses keyboardHeight from native events (not viewport calculations)
          - Matches 0.25s transition timing with input transform animation below */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          height: 100%;
          padding-bottom: calc(
            ${isKeyboardVisible
              ? `${keyboardHeight}px + ${spacing.regular} + ${spacing.regular}`
              : `${spacing.giga} + ${buildPageSpacing(spacing.kilo, 'BOTTOM')}`}
          );
          transition: padding-bottom 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        `}
      >
        {/* Component to show the feedback when the rating dialog is activated */}
        <FeedbackTakeover isShown={isFeedbackTakeoverShown} onClose={() => setIsFeedbackTakeoverShown(false)} />
        {isNavigatedFrom ? (
          <Header
            headingText={t('chat.title')}
            onBack={() => history.goBack()}
            css={css`
              border-bottom: 0.5px solid ${colors.cloud};
              html[data-theme='dark'] & {
                border-bottom-color: ${colors.tungsten};
              }
            `}
            rightAction={
              <Button
                onClick={handleNewChat}
                css={css`
                  ${fontStyles.dog}
                  padding: ${spacing.milli} ${spacing.deci};
                  color: ${colors.silver};
                  min-width: unset;
                  white-space: nowrap;

                  &:hover {
                    color: ${colors.petrolBlue};
                  }

                  html[data-theme='dark'] & {
                    color: ${colors.cloud};
                    &:hover {
                      color: ${colors.skyBlue};
                    }
                  }
                `}
              >
                {t('chat.newChat')}
              </Button>
            }
          />
        ) : (
          <StickyHeader
            headerCss={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.centi};
              `}
            >
              {/* Hamburger menu button */}
              <button
                onClick={() => setIsDrawerOpen(true)}
                css={css`
                  background: none;
                  border: none;
                  padding: ${spacing.milli};
                  cursor: pointer;
                  color: ${colors.liquorice};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border-radius: 6px;
                  transition: background-color 0.15s ease;
                  margin-left: -${spacing.milli};

                  &:hover {
                    background-color: rgba(0, 0, 0, 0.05);
                  }

                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                    &:hover {
                      background-color: rgba(255, 255, 255, 0.1);
                    }
                  }
                `}
                aria-label={t('chat.openHistory')}
                type="button"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
                </svg>
              </button>

              <h1
                css={css`
                  color: ${colors.liquorice};
                  margin: 0;
                  ${fontStyles.shark}
                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                  }
                `}
              >
                {t('chat.title')}
              </h1>
              {isSubscribed && (
                <div
                  css={css`
                    background: ${colors.petrolBlue};
                    color: ${colors.sugar};
                    padding: 2px ${spacing.milli};
                    border-radius: 4px;
                    ${fontStyles.flea}
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    html[data-theme='dark'] & {
                      background: ${colors.skyBlue};
                    }
                  `}
                >
                  {t('chat.pro')}
                </div>
              )}
            </div>
            <Button
              onClick={handleNewChat}
              css={css`
                ${fontStyles.dog}
                color: ${colors.petrolBlue};
                flex-shrink: 0;
                white-space: nowrap;

                &:hover {
                  opacity: 0.8;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                  &:hover {
                    opacity: 0.8;
                  }
                }
              `}
            >
              {t('chat.newChat')}
            </Button>
          </StickyHeader>
        )}

        {/* Messages container with GPU acceleration for smooth scrolling during keyboard animation */}
        <div
          css={css`
            flex: 1;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            min-height: 0;
            /* DO NOT REMOVE: Hardware acceleration prevents scrolling jank during keyboard transitions */
            transform: translateZ(0);
            will-change: auto;
          `}
        >
          {messages.length === 0 ? (
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: ${spacing.deci};
                padding: ${spacing.centi} 0;
              `}
            >
              <div
                css={css`
                  width: 60px;
                  height: 60px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0;
                  overflow: hidden;
                  background: transparent;
                  border: 2px solid ${colors.fog};
                  margin-top: ${spacing.regular};

                  html[data-theme='dark'] & {
                    border-color: ${colors.tungsten};
                  }
                `}
              >
                <img
                  src={BloomIcon}
                  alt="Bloom Logo"
                  css={css`
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                  `}
                />
              </div>
              <h2
                css={css`
                  ${fontStyles.lion}
                  color: ${colors.liquorice};
                  margin: 0;
                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                  }
                `}
              >
                {t('chat.emptyState')}
              </h2>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: ${spacing.centi};
                  width: 100%;
                  max-width: 768px;
                  padding: 0 ${spacing.regular};
                `}
              >
                {defaultQuestions.map((question) => (
                  <button
                    key={question.prompt}
                    onClick={() => handleSendMessage(question.display)}
                    css={css`
                      padding: 16px 20px;
                      border: 0.5px solid rgba(0, 0, 0, 0.2);
                      border-radius: 16px;
                      background: ${colors.snow};
                      color: ${colors.liquorice};
                      text-align: left;
                      cursor: pointer;
                      ${fontStyles.thinFlea}
                      position: relative;
                      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
                      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

                      &::after {
                        content: '→';
                        position: absolute;
                        right: 20px;
                        top: 50%;
                        transform: translateY(-50%);
                        opacity: 0.5;
                        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                        color: ${colors.silver};
                      }

                      &:hover {
                        background: rgba(247, 247, 248, 1);
                        border-color: rgba(0, 0, 0, 0.12);
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                        transform: translateY(-1px);

                        &::after {
                          transform: translateY(-50%) translateX(3px);
                          opacity: 0.8;
                          color: ${colors.petrolBlue};
                        }
                      }

                      &:active {
                        transform: translateY(0px);
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
                      }

                      html[data-theme='dark'] & {
                        background: rgba(255, 255, 255, 0.02);
                        border-color: rgba(255, 255, 255, 0.08);
                        color: ${colors.sugar};
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

                        &::after {
                          color: ${colors.cloud};
                        }

                        &:hover {
                          background: rgba(255, 255, 255, 0.04);
                          border-color: rgba(255, 255, 255, 0.12);
                          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

                          &::after {
                            color: ${colors.skyBlue};
                          }
                        }
                      }
                    `}
                  >
                    {question.display}
                  </button>
                ))}
              </div>
              {threads.some((t) => t.messages.length > 0) && (
                <button
                  onClick={() => setIsDrawerOpen(true)}
                  css={css`
                    background: none;
                    border: none;
                    color: ${colors.petrolBlue};
                    ${fontStyles.cat}
                    cursor: pointer;
                    padding: ${spacing.centi} 0;
                    text-decoration: none;

                    &:hover {
                      text-decoration: underline;
                    }

                    html[data-theme='dark'] & {
                      color: ${colors.skyBlue};
                    }
                  `}
                >
                  {t('chat.openHistory')}
                </button>
              )}
            </div>
          ) : (
            <>
              {displayMessages.map((message, index) => {
                return (
                  <div
                    key={index}
                    css={css`
                      margin-bottom: ${spacing.deci};
                      ${message.role === 'user' ? 'display: flex; flex-direction: column; align-items: flex-end;' : ''}
                    `}
                  >
                    <div
                      css={css`
                        ${fontStyles.thinFlea}
                        ${message.role === 'user' ? 'max-width: 85%;' : 'width: 100%;'}
                        ${message.role === 'user'
                          ? `
                          background-color: ${colors.petrolBlue};
                          color: ${colors.sugar};
                          padding: ${spacing.deci};
                          border-radius: 12px;
                        `
                          : `
                          color: ${colors.liquorice};
                          -webkit-user-select: text;
                          user-select: text;
                          html[data-theme='dark'] & {
                            color: ${colors.sugar};
                          }
                        `}

                        /* Markdown styles */
                        & p {
                          margin: 0 0 ${spacing.centi} 0;
                          &:last-child {
                            margin-bottom: 0;
                          }
                        }
                        & h1 {
                          ${fontStyles.lion}
                          margin: 0 0 ${spacing.centi} 0;
                        }
                        & h2 {
                          ${fontStyles.dog}
                          margin: 0 0 ${spacing.centi} 0;
                        }
                        & ul,
                        & ol {
                          margin: ${spacing.centi} 0;
                          padding-left: ${spacing.regular};
                        }
                        & li {
                          margin: ${spacing.milli} 0;
                        }
                        & code {
                          background-color: rgba(0, 0, 0, 0.1);
                          padding: 2px 4px;
                          border-radius: 4px;
                        }
                        & pre {
                          background-color: rgba(0, 0, 0, 0.1);
                          padding: ${spacing.milli};
                          border-radius: 4px;
                          overflow-x: auto;
                          margin: ${spacing.milli} 0;
                        }
                        & strong {
                          font-weight: 500;
                        }

                        & em {
                          font-style: normal;
                        }

                        & a {
                          ${message.role === 'user'
                            ? `color: ${colors.sugar} !important; text-decoration: underline;`
                            : `color: ${colors.petrolBlue}; text-decoration: underline;
                               html[data-theme='dark'] & {
                                 color: ${colors.skyBlue};
                               }`}
                        }

                        /* Table styles */
                        & .table-wrapper {
                          overflow-x: auto;
                          -webkit-overflow-scrolling: touch;
                          margin: ${spacing.deci} 0;
                          border-radius: 8px;
                          border: 1px solid ${colors.cloud};
                        }

                        & table {
                          border-collapse: collapse;
                          width: 100%;
                          display: table;
                          font-size: 0.9em;
                          margin: 0;
                        }

                        & th,
                        & td {
                          border: none;
                          border-bottom: 1px solid ${colors.cloud};
                          padding: ${spacing.deci};
                          text-align: left;
                          vertical-align: top;
                          white-space: normal;
                          word-break: normal;
                          line-height: 1.5;
                        }

                        & th {
                          background-color: rgba(0, 0, 0, 0.05);
                          font-weight: 600;
                          min-width: 120px;
                          white-space: nowrap;
                          border-bottom: 2px solid ${colors.cloud};
                        }

                        & td {
                          /* Handle line breaks within cells */
                          white-space: pre-line;

                          & br {
                            display: block;
                            content: '';
                            margin-top: 0.5em;
                          }

                          /* Handle lists within cells */
                          & ul,
                          & ol {
                            margin: 0.5em 0;
                            padding-left: 1.5em;
                          }

                          & li {
                            margin: 0.25em 0;
                          }

                          /* Bold formatting in table cells (italic disabled) */
                          & strong {
                            font-weight: 500;
                          }

                          & em {
                            font-style: normal;
                          }
                        }

                        & tr:nth-of-type(even) {
                          background-color: rgba(0, 0, 0, 0.02);
                        }

                        & tr:last-child td {
                          border-bottom: none;
                        }

                        html[data-theme='dark'] & {
                          & .table-wrapper {
                            border-color: ${colors.tungsten};
                          }

                          & th,
                          & td {
                            border-color: ${colors.tungsten};
                          }

                          & th {
                            background-color: rgba(255, 255, 255, 0.05);
                            border-bottom-color: ${colors.tungsten};
                          }

                          & tr:nth-of-type(even) {
                            background-color: rgba(255, 255, 255, 0.02);
                          }
                        }
                      `}
                    >
                      {/* Show tool outputs (context) */}
                      {message.toolOutputs && message.toolOutputs.length > 0 && (
                        <div
                          css={css`
                            margin-bottom: ${spacing.deci};
                          `}
                        >
                          {message.toolOutputs.map((toolOutput, index) => (
                            <ToolOutputDisplay
                              key={index}
                              toolOutput={toolOutput}
                              showLatestBadge={index === 0 && toolOutput.tool_name === 'todo_write'}
                            />
                          ))}
                        </div>
                      )}

                      {/* Show attached images for user messages */}
                      {message.role === 'user' && message.images && message.images.length > 0 && (
                        <div
                          css={css`
                            display: flex;
                            gap: ${spacing.centi};
                            flex-wrap: wrap;
                            margin-bottom: ${spacing.centi};
                          `}
                        >
                          {message.images.map((image) => (
                            <img
                              key={image.id}
                              src={image.dataUrl}
                              alt={image.fileName}
                              css={css`
                                max-width: 200px;
                                max-height: 200px;
                                border-radius: 8px;
                                object-fit: cover;
                              `}
                            />
                          ))}
                        </div>
                      )}

                      {/* Finally show message content */}
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
                        rehypePlugins={[rehypeRaw as any, rehypeKatex as any]}
                        components={{
                          table: ({ children, ...props }) => (
                            <div className="table-wrapper">
                              <table {...props}>{children}</table>
                            </div>
                          ),
                          td: ({ children, style }) => <td style={style}>{children}</td>,
                          th: ({ children, style }) => <th style={style}>{children}</th>,
                          a: ({ href, children, ...props }) => {
                            const isInternal = href?.startsWith('/') || href === '#subscribe';
                            if (isInternal) {
                              if (href === '#subscribe') {
                                return (
                                  <Link
                                    to="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      stores.showPaymentModalByPreference('chat_page_subscribe_link');
                                    }}
                                    {...props}
                                  >
                                    {children}
                                  </Link>
                                );
                              }
                              return (
                                <Link to={href || ''} {...props}>
                                  {children}
                                </Link>
                              );
                            }
                            return (
                              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                {children}
                              </a>
                            );
                          },
                        }}
                      >
                        {cleanMarkdownContent(extractTextFromContent(message.content))}
                      </ReactMarkdown>

                      {/* Finally show collection cards */}
                      {message.collectionCards && <ChatCollectionCards collectionCardsData={message.collectionCards} />}
                    </div>
                    {/* Sprint 1c: Signal-style message status indicators */}
                    {message.role === 'user' && message.status && (
                      <div
                        css={css`
                          display: flex;
                          justify-content: flex-end;
                          margin-top: 2px;
                          ${fontStyles.thinFlea}
                          color: ${message.status === 'failed' ? colors.orange : colors.silver};
                          font-size: 0.7rem;
                        `}
                      >
                        {message.timestamp && (
                          <span
                            css={css`
                              margin-right: 3px;
                            `}
                          >
                            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {message.status === 'sending' && <span>&#128339;</span>}
                        {message.status === 'sent' && <span>&#10003;</span>}
                        {message.status === 'failed' && (
                          <span
                            css={css`
                              cursor: pointer;
                            `}
                            onClick={() => setRetryTimestamp(message.timestamp || null)}
                          >
                            &#10007; {t('chat.tapToRetry')}
                          </span>
                        )}
                      </div>
                    )}
                    {retryTimestamp === message.timestamp && (
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: ${spacing.milli};
                          margin-top: ${spacing.milli};
                          ${fontStyles.mouse};
                          color: ${colors.orange};
                        `}
                      >
                        <span>{t('chat.failedResponse')}</span>
                        <button
                          css={css`
                            background: transparent;
                            border: 1px solid ${colors.petrolBlue};
                            color: ${colors.petrolBlue};
                            border-radius: ${borderRadius};
                            padding: ${spacing.milli} ${spacing.centi};
                            cursor: pointer;
                            font-family: ${fontFamilies.helveticaNowText};
                            font-weight: 500;
                            &:hover {
                              background-color: rgba(23, 110, 193, 0.1);
                            }
                            html[data-theme='dark'] & {
                              color: ${colors.skyBlue};
                              border-color: ${colors.skyBlue};
                            }
                          `}
                          onClick={handleRetry}
                        >
                          {t('chat.retry')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {!isLoading && (isSuggestionsLoading || suggestedQuestions.length > 0) && (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: ${spacing.centi};
                    margin-top: ${spacing.milli};

                    @keyframes fadeInUp {
                      from {
                        opacity: 0;
                        transform: translateY(10px);
                      }
                      to {
                        opacity: 1;
                        transform: translateY(0);
                      }
                    }
                  `}
                >
                  {isSuggestionsLoading ? (
                    <Spinner size="sm" color={colors.cloud} />
                  ) : (
                    suggestedQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => handleSendMessage(question)}
                        css={css`
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          gap: ${spacing.centi};
                          padding: 16px 20px;
                          background: ${colors.snow};
                          border: 0.5px solid rgba(0, 0, 0, 0.2);
                          border-radius: 24px;
                          cursor: pointer;
                          text-align: left;
                          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
                          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                          animation: fadeInUp 0.3s ease-out;
                          animation-delay: ${index * 50}ms;
                          animation-fill-mode: both;

                          &::after {
                            content: '→';
                            opacity: 0.5;
                            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                            flex-shrink: 0;
                            color: ${colors.silver};
                          }

                          &:hover {
                            background: rgba(247, 247, 248, 1);
                            border-color: rgba(0, 0, 0, 0.12);
                            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                            transform: translateY(-1px);

                            &::after {
                              transform: translateX(3px);
                              opacity: 0.8;
                              color: ${colors.petrolBlue};
                            }
                          }

                          &:active {
                            transform: translateY(0px);
                            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
                          }

                          html[data-theme='dark'] & {
                            background: rgba(255, 255, 255, 0.02);
                            border-color: rgba(255, 255, 255, 0.08);
                            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

                            &::after {
                              color: ${colors.cloud};
                            }

                            &:hover {
                              background: rgba(255, 255, 255, 0.04);
                              border-color: rgba(255, 255, 255, 0.12);
                              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

                              &::after {
                                color: ${colors.skyBlue};
                              }
                            }
                          }
                        `}
                      >
                        <span
                          css={css`
                            ${fontStyles.thinFlea}
                            color: ${colors.petrolBlue};
                            line-height: 1.4;
                            flex: 1;

                            html[data-theme='dark'] & {
                              color: ${colors.sugar};
                            }
                          `}
                        >
                          {question}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {/* Loading States - Show reasoning above tool outputs */}
          {/* Sprint 1a: Show loading when isSending OR isLoading */}
          {(isSending || isLoading) && (
            <div
              css={css`
                margin: ${spacing.regular} 0;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
              `}
            >
              {/* Show tool outputs (context) - todo first, then others */}
              {(latestTodoOutput || toolOutputs.length > 0) && (
                <div
                  css={css`
                    width: 100%;
                    margin-bottom: ${spacing.deci};
                  `}
                >
                  {/* Show latest todo output first */}
                  {latestTodoOutput && (
                    <ToolOutputDisplay key="todo-latest" toolOutput={latestTodoOutput} showLatestBadge={true} />
                  )}

                  {/* Show other tool outputs */}
                  {toolOutputs.map((toolOutput, index) => (
                    <ToolOutputDisplay key={index} toolOutput={toolOutput} />
                  ))}
                </div>
              )}

              {/* Then show loading message (simplified - no reasoning display here) */}
              <LoadingMessage progressText={progressText} streamingMessage={streamingMessage} />
            </div>
          )}
          <div
            ref={messagesEndRef}
            css={css`
              padding-bottom: ${spacing.regular};
            `}
          />
        </div>

        {/* CRITICAL: WhatsApp-style input container with transform animation
            DO NOT REPLACE WITH BOTTOM POSITIONING - causes flickering!
            
            Key implementation details:
            - Fixed to bottom: 0 (never changes)
            - Uses translateY() with negative keyboardHeight to move up smoothly  
            - Transform syncs with native keyboard animation timing (0.25s)
            - Hardware accelerated with translateZ(0) and backface-visibility
            - keyboardHeight comes from keyboardWillShow events in index.tsx */}
        <div
          css={css`
            position: fixed;
            bottom: 0;
            ${Capacitor.getPlatform() === 'android'
              ? ''
              : `transform: translateY(${isKeyboardVisible ? -keyboardHeight : 0}px) translateZ(0);`}
            left: 0;
            right: 0;
            z-index: 100;
            padding: ${spacing.deci} ${buildPageSpacing(spacing.deci, 'LEFT')}
              ${Capacitor.getPlatform() === 'android'
                ? `${!isKeyboardVisible ? buildPageSpacing('96px', 'BOTTOM') : '10px'}`
                : `calc(
                    ${!isKeyboardVisible ? spacing.giga : '0px'} +
                      ${buildPageSpacing(isKeyboardVisible ? '0px' : spacing.deci, 'BOTTOM')}
                  )`}
              ${buildPageSpacing(spacing.deci, 'RIGHT')};
            background-color: ${colors.sugar};
            backdrop-filter: blur(10px);
            border-top: 0.5px solid rgba(0, 0, 0, 0.2);
            will-change: transform;
            transition: transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
            backface-visibility: hidden;

            html[data-theme='dark'] & {
              background-color: rgba(16, 16, 16, 0.95);
              border-top-color: rgba(255, 255, 255, 0.08);
            }
          `}
        >
          <div
            css={css`
              max-width: 768px;
              margin: 0 auto;
            `}
          >
            {/* Sprint 1d: Subtle offline indicator */}
            {!isOnline && isNetworkInitialized && (
              <div
                css={css`
                  text-align: center;
                  padding: ${spacing.milli} 0;
                  ${fontStyles.thinFlea}
                  color: ${colors.orange};
                  margin-bottom: ${spacing.milli};
                `}
              >
                {t('chat.offlineMessage')}
              </div>
            )}
            <ChatInput
              onSendMessage={handleSendMessage}
              isLoading={isSending || isLoading}
              disabled={(!isOnline && isNetworkInitialized) || isSending || isLoading}
            />
          </div>
          {!isSubscribed && (
            <div
              css={css`
                text-align: center;
                margin-top: 12px;
                ${fontStyles.thinMouse}
                color: ${messageCount >= MAX_FREE_MESSAGES ? colors.orange : colors.silver};
              `}
            >
              {t('chat.freeMessagesLeft', { remaining: MAX_FREE_MESSAGES - messageCount, total: MAX_FREE_MESSAGES })}
              {messageCount >= MAX_FREE_MESSAGES && (
                <>
                  {' '}
                  <span
                    onClick={() => stores.showPaymentModalByPreference('chat_message_limit_subscribe')}
                    css={css`
                      color: ${colors.petrolBlue};
                      text-decoration: none;
                      cursor: pointer;
                    `}
                  >
                    {t('chat.subscribe')}
                  </span>
                </>
              )}
            </div>
          )}
          {(() => {
            const selectedModelId = getChatModelPreference();
            if (!isSubscribed || !selectedModelId || !isPremiumModel(selectedModelId)) return null;
            const { used, remaining } = getPremiumModelUsage();
            const modelName = CHAT_MODELS.find((m) => m.id === selectedModelId)?.displayName ?? 'Premium';
            return (
              <div
                css={css`
                  text-align: center;
                  margin-top: 12px;
                  ${fontStyles.thinMouse}
                  color: ${remaining <= 0 ? colors.orange : colors.silver};
                `}
              >
                {t('chat.modelMessagesLeft', { modelName, remaining, total: used + remaining })}
              </div>
            );
          })()}
        </div>
      </div>
    </Page>
  );
};

export default ChatPage;
